"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getIsIndefiniteStatus = void 0;
exports.transformIncomingOfferData = transformIncomingOfferData;
exports.transformWorkingTimeDateToClientTimezone = transformWorkingTimeDateToClientTimezone;
exports.getFormTurnUpDate = getFormTurnUpDate;
exports.areOfferValuesChanged = areOfferValuesChanged;
exports.prepareOfferAssetGalleries = prepareOfferAssetGalleries;
const moment_1 = __importDefault(require("moment"));
const date_fns_1 = require("date-fns");
const lodash_1 = require("lodash");
const types_1 = require("@src/types");
const get_timezone_from_offset_1 = require("@src/backend/helpers/get-timezone-from-offset");
function transformTurnUpDate(turnUpDate, createdAt) {
    if (!turnUpDate) {
        return {
            [types_1.EOfferBasicInfoFormField.turnUpDate]: null,
            [types_1.EOfferBasicInfoFormField.turnUpDateAsap]: false,
        };
    }
    const isTurnUpDateInPast = createdAt > turnUpDate;
    if (isTurnUpDateInPast) {
        return {
            [types_1.EOfferBasicInfoFormField.turnUpDate]: null,
            [types_1.EOfferBasicInfoFormField.turnUpDateAsap]: true,
        };
    }
    return {
        [types_1.EOfferBasicInfoFormField.turnUpDate]: (0, moment_1.default)(turnUpDate),
        [types_1.EOfferBasicInfoFormField.turnUpDateAsap]: false,
    };
}
function transformDriverLicences(driverLicences) {
    return driverLicences === null || driverLicences === void 0 ? void 0 : driverLicences.map(driverLicence => ({
        [types_1.EOfferBasicInfoFormField.driverLicence]: driverLicence.drivingLicenceType,
        [types_1.EOfferBasicInfoFormField.mileage]: driverLicence.mileage,
    }));
}
function transformRequiredLanguages(requiredLanguages) {
    return requiredLanguages === null || requiredLanguages === void 0 ? void 0 : requiredLanguages.map(language => ({
        [types_1.EOfferBasicInfoFormField.languageCode]: language.languageCode,
        [types_1.EOfferBasicInfoFormField.languageLevel]: language.level,
    }));
}
function transformWorkingTime(workingTime, fallbackTimezone) {
    if (!workingTime) {
        return;
    }
    const clientUtcOffset = (0, moment_1.default)().utcOffset();
    // set BE time as UTC time === +0000
    const utcTime = moment_1.default.utc(workingTime, 'HH:mm');
    // timeInOfferTimezone is time in given TZ with offer (or fallback) offset
    const timeInOfferTimezone = (0, moment_1.default)(utcTime).tz(fallbackTimezone);
    // for having right TZ in timepicker, we need to set utcOffset for client and keep time value
    const timeInOfferTimezoneWithClientOffset = timeInOfferTimezone.utcOffset(clientUtcOffset, true);
    return timeInOfferTimezoneWithClientOffset;
}
function transformIsIndefiniteStatus(isIndefinite) {
    if (isIndefinite === null) {
        return null;
    }
    if (Boolean(isIndefinite) === true) {
        return types_1.ContractDurationType.indefinite;
    }
    return types_1.ContractDurationType.definite;
}
function transformIncomingOfferData(offerData, companyTimezone) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const isHomeOffice = offerData.addressId === null;
    const turnUpDateFields = transformTurnUpDate(offerData.turnUpDate, offerData.createdAt);
    const clientUtcOffset = (0, moment_1.default)().utcOffset();
    const fallbackTimezone = offerData.timezone || companyTimezone || (0, get_timezone_from_offset_1.getSingleTimezoneFromArray)((0, get_timezone_from_offset_1.getOffsetTimezoneNames)(clientUtcOffset));
    const areSkillTagsRated = ((_a = offerData.skillTagsRates) === null || _a === void 0 ? void 0 : _a.length) && offerData.skillTagsRates.every(tagRate => tagRate.value);
    const offerPaymentType = {
        [types_1.EOfferPaymentTypeFormField.billingProfileId]: offerData.companyBillingProfileId,
        [types_1.EOfferPaymentTypeFormField.paymentType]: offerData.paymentType,
    };
    const offerBasicInfo = {
        [types_1.EOfferBasicInfoFormField.title]: offerData.title,
        [types_1.EOfferBasicInfoFormField.redirectUrl]: offerData.redirectUrl,
        [types_1.EOfferBasicInfoFormField.offerTypeId]: offerData.offerTypeId,
        [types_1.EOfferBasicInfoFormField.homeOffice]: isHomeOffice,
        [types_1.EOfferBasicInfoFormField.jobArea]: offerData.jobAreas,
        [types_1.EOfferBasicInfoFormField.description]: offerData.description,
        [types_1.EOfferBasicInfoFormField.required]: offerData.required,
        [types_1.EOfferBasicInfoFormField.niceToHave]: offerData.niceToHave,
        [types_1.EOfferBasicInfoFormField.deadline]: offerData.deadline ? (0, moment_1.default)(offerData.deadline) : null,
        [types_1.EOfferBasicInfoFormField.isPaid]: Boolean(offerData.isPaid),
        [types_1.EOfferBasicInfoFormField.benefits]: offerData.benefits,
        [types_1.EOfferBasicInfoFormField.durationMin]: (_b = offerData.offerContractDuration) === null || _b === void 0 ? void 0 : _b.durationMin,
        [types_1.EOfferBasicInfoFormField.durationMax]: (_c = offerData.offerContractDuration) === null || _c === void 0 ? void 0 : _c.durationMax,
        [types_1.EOfferBasicInfoFormField.durationType]: (_d = offerData.offerContractDuration) === null || _d === void 0 ? void 0 : _d.durationType,
        [types_1.EOfferBasicInfoFormField.contractTypeId]: offerData.contractTypeId,
        [types_1.EOfferBasicInfoFormField.salaryRate]: offerData.salaryRate,
        [types_1.EOfferBasicInfoFormField.salaryAmountFrom]: offerData.salaryAmountFrom,
        [types_1.EOfferBasicInfoFormField.salaryAmountTo]: offerData.salaryAmountTo,
        [types_1.EOfferBasicInfoFormField.salaryCurrency]: offerData.salaryCurrency || '€',
        [types_1.EOfferBasicInfoFormField.salaryDescription]: offerData.salaryDescription,
        [types_1.EOfferBasicInfoFormField.resumeRequired]: Boolean(offerData.resumeRequired),
        [types_1.EOfferBasicInfoFormField.motivationalRequired]: Boolean(offerData.motivationalRequired),
        [types_1.EOfferBasicInfoFormField.address]: offerData.address
            ? {
                country: (_e = offerData.address) === null || _e === void 0 ? void 0 : _e.country,
                formatted_address: (_f = offerData.address) === null || _f === void 0 ? void 0 : _f.formattedAddress,
                google_place_id: (_g = offerData.address) === null || _g === void 0 ? void 0 : _g.googlePlaceId,
            }
            : undefined,
        [types_1.EOfferBasicInfoFormField.contact_email]: offerData.contact_email,
        ...turnUpDateFields,
        [types_1.EOfferBasicInfoFormField.driverLicences]: transformDriverLicences(offerData.offerDrivingLicences),
        [types_1.EOfferBasicInfoFormField.languages]: transformRequiredLanguages(offerData.offerRequiredLanguages),
        [types_1.EOfferBasicInfoFormField.workingTimeFrom]: transformWorkingTime((_h = offerData.workingTime) === null || _h === void 0 ? void 0 : _h.timeFrom, fallbackTimezone),
        [types_1.EOfferBasicInfoFormField.workingTimeTo]: transformWorkingTime((_j = offerData.workingTime) === null || _j === void 0 ? void 0 : _j.timeTo, fallbackTimezone),
        [types_1.EOfferBasicInfoFormField.workingTimeHours]: (_k = offerData.workingTime) === null || _k === void 0 ? void 0 : _k.timeGrant,
        [types_1.EOfferBasicInfoFormField.workingTimeType]: (_l = offerData.workingTime) === null || _l === void 0 ? void 0 : _l.timeType,
        [types_1.EOfferBasicInfoFormField.workingTimeInfo]: (_m = offerData.workingTime) === null || _m === void 0 ? void 0 : _m.additionalInformation,
        [types_1.EOfferBasicInfoFormField.skillTags]: offerData.skillTags,
        [types_1.EOfferBasicInfoFormField.rateSkillTagsSwitch]: areSkillTagsRated,
        [types_1.EOfferBasicInfoFormField.skillTagsRates]: offerData.skillTagsRates,
        [types_1.EOfferBasicInfoFormField.timezone]: fallbackTimezone,
        [types_1.EOfferBasicInfoFormField.salaryType]: offerData.salaryType,
        [types_1.EOfferBasicInfoFormField.isIndefinite]: transformIsIndefiniteStatus((_p = (_o = offerData.offerContractDuration) === null || _o === void 0 ? void 0 : _o.isIndefinite) !== null && _p !== void 0 ? _p : null),
    };
    const offerAssets = {};
    if ((_q = offerData.galleries) === null || _q === void 0 ? void 0 : _q.length) {
        offerAssets[types_1.EOfferAssetsFormField.galleries] = offerData.galleries;
    }
    return { offerBasicInfo, offerAssets, offerPaymentType };
}
function transformWorkingTimeDateToClientTimezone(timezone, workingTime) {
    if (!workingTime) {
        return null;
    }
    const clientUtcOffset = (0, moment_1.default)().utcOffset();
    const addressOffset = (0, moment_1.default)().tz(timezone).utcOffset() || clientUtcOffset;
    return ((0, moment_1.default)(workingTime)
        .utcOffset(-addressOffset + clientUtcOffset)
        .format('HH:mm:ss') || null);
}
function getFormTurnUpDate(offer) {
    return offer[types_1.EOfferBasicInfoFormField.turnUpDateAsap]
        ? (0, date_fns_1.startOfYesterday)()
        : offer[types_1.EOfferBasicInfoFormField.turnUpDate];
}
const getIsIndefiniteStatus = (status) => {
    if (status === null) {
        return null;
    }
    if (status === types_1.ContractDurationType.indefinite) {
        return true;
    }
    return false;
};
exports.getIsIndefiniteStatus = getIsIndefiniteStatus;
function areOfferValuesChanged({ offerPaymentTypeValues, offerPaymentTypeValuesInitialValues, offerBasicInfoValues, offerBasicInfoInitialValues, offerAssetsValues, offerAssetsInitialValues, isEditingExistingDraft = false, }) {
    const removeFalsy = (obj) => {
        const clone = (0, lodash_1.cloneDeep)(obj);
        Object.keys(clone).forEach(key => {
            const value = clone[key];
            if (value === null || value === undefined || (value === null || value === void 0 ? void 0 : value.length) === 0) {
                delete clone[key];
            }
        });
        return clone;
    };
    const arePaymentTypeValuesChanged = !(0, lodash_1.isEqual)(removeFalsy(offerPaymentTypeValues), removeFalsy(offerPaymentTypeValuesInitialValues)) &&
        !(0, lodash_1.isEmpty)(offerPaymentTypeValues);
    const areBasicInfoValuesChanged = !(0, lodash_1.isEqual)(removeFalsy(offerBasicInfoValues), removeFalsy(offerBasicInfoInitialValues)) &&
        !(0, lodash_1.isEmpty)(offerBasicInfoValues);
    const areAssetsValuesChanged = !(0, lodash_1.isEqual)(removeFalsy(offerAssetsValues), removeFalsy(offerAssetsInitialValues)) && !(0, lodash_1.isEmpty)(offerAssetsValues);
    // Only consider payment type changes if editing an existing draft
    // For new offers, payment type changes alone shouldn't trigger a draft save
    const shouldConsiderPaymentTypeChanges = isEditingExistingDraft && arePaymentTypeValuesChanged;
    return areBasicInfoValuesChanged || areAssetsValuesChanged || shouldConsiderPaymentTypeChanges;
}
function prepareOfferAssetGalleries(galleries) {
    if (!(galleries === null || galleries === void 0 ? void 0 : galleries.length)) {
        return null;
    }
    return galleries.map(gallery => ({
        title: gallery[types_1.EOfferGalleryKey.title],
        isTitleGallery: gallery[types_1.EOfferGalleryKey.isTitleGallery],
        uuid: gallery[types_1.EOfferGalleryKey.uuid],
        assetIds: gallery[types_1.EOfferGalleryKey.assets].map(asset => asset.id),
    }));
}
